
import Vue from 'vue';
import Component from 'vue-class-component';
import PhotosModule from '@/store/modules/Photos';
import SessionsModule from '@/store/modules/Sessions';
import store from '@/store/index';
import { getModule } from 'vuex-module-decorators';
import { Watch } from 'vue-property-decorator';
import { Session } from 'inspector';
import Dropzone, { FileStructure } from '@/decorators/Dropzone';
import WatermarkDialog from '@/components/WatermarkDialog.vue';
import Watermark from '../model/Watermark';
import UploadModule from '@/store/modules/Upload';
import UserModule from '@/store/modules/User';
import {
    confirmRawConversion,
    RawConversionDecision,
    waitForRawConversionDialogClose,
} from '@/service/rawConversionConfirmation';

const photosModule = getModule(PhotosModule, store);
const sessionsModule = getModule(SessionsModule, store);
const uploadModule = getModule(UploadModule, store);
const userModule = getModule(UserModule, store);

@Component({
    components: {
        WatermarkDialog,
    },
})
@Dropzone
export default class UploadTray extends Vue {
    private fileBuffer: File[] = [];
    private trayActive: boolean = false;
    private dragHoverActive: boolean = false;
    private watermarkDialog: boolean = false;

    get uploadInProgress() {
        return uploadModule.uploadInProgress;
    }

    public triggerInput() {
        const tray = this.$el as HTMLElement;
        const rect = tray.getBoundingClientRect();
        this.$emit('trayClick', {
            x: rect.left + (rect.width / 2),
            y: rect.top + (rect.height / 2),
        });
        (this.$refs.fileInput as HTMLInputElement).click();
    }

    public cancel() {
        uploadModule.cancelAll();
    }

    public onHover() {
        this.$emit('hover');
    }

    public onLeave() {
        this.$emit('leave');
    }

    public mounted() {
        (this.$refs.fileInput as HTMLInputElement).addEventListener('change', (e) => {
            const fileList = (e.target as HTMLInputElement).files;
            if (fileList) {
                const files: File[] = [];

                for (let i = 0; i < fileList.length; i++) {
                    const file = fileList.item(i);
                    if (file) {
                        files.push(file);
                    }
                }

                this.handleFiles(files);
            }
        });
        this.$on('drop', (structure: FileStructure) => this.handleFiles(structure.allFiles));
        this.$on('globalDragstart', () => {
            this.$el.classList.add('active');
            this.trayActive = true;
        });
        this.$on('globalDragend', () => {
            this.$el.classList.remove('active');
            this.$el.classList.remove('draghover');
            this.trayActive = false;
            this.dragHoverActive = false;
        });
        this.$on('dragenter', () => {
            this.$el.classList.add('draghover');
            this.dragHoverActive = true;
        });
        this.$on('dragleave', () => {
            this.$el.classList.remove('draghover');
            this.dragHoverActive = false;
        });
    }

    private async handleFiles(buffer: File[], rawConversionDecision?: RawConversionDecision) {
        if (!rawConversionDecision) {
            rawConversionDecision = await confirmRawConversion(buffer);
            if (!rawConversionDecision.confirmed) {
                return;
            }
        }

        if (buffer.length > 0 && !this.currentSession && this.creatingSession) {
            const newSession = await sessionsModule.createUnnamedSession(userModule.user!.id);
            await new Promise<void>((resolve, reject) => this.$router.push({
                name: 'editSession',
                params: {
                    id: newSession.id,
                },
            }, () => resolve(), () => reject()));
            this.handleFiles(buffer, rawConversionDecision);
        } else if (buffer.length > 0 && this.currentSession) {
            if (!this.currentSession.watermark) {
                await waitForRawConversionDialogClose(buffer);
                this.watermarkDialog = true;
                await sessionsModule.chooseWatermark({
                    sessionId: this.currentSession.id,
                    watermark: await new Promise<Watermark>((resolve) =>
                        (this.$refs.watermarkDialog as Vue).$once('watermarkResolved', resolve),
                    ),
                });
            }
            uploadModule.uploadFiles({
                session: this.currentSession,
                files: buffer,
                rawConversionDecision,
            });
            this.fileBuffer = [];
        }
    }

    @Watch('currentSession')
    private currentSessionHandler(currentSession: Session) {
        if (currentSession) {
            this.handleFiles(this.fileBuffer);
        }
    }

    get currentSession() {
        return sessionsModule.currentSession;
    }

    get creatingSession() {
        return sessionsModule.creatingSession;
    }
}
